import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  SignedIn,
  SignedOut,
  SignIn,
  useOrganizationList,
} from '@clerk/clerk-react';

const APP_REDIRECT_PATH = '/app';

const AcceptInvitationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isLoaded, userInvitations } = useOrganizationList({
    userInvitations: {
      infinite: true,
      keepPreviousData: true,
    },
  });
  const [error, setError] = useState('');
  const [acceptingId, setAcceptingId] = useState(null);
  const autoAcceptedRef = useRef(false);

  const ticket = searchParams.get('__clerk_ticket');
  const status = searchParams.get('__clerk_status');
  const invitations = useMemo(
    () => userInvitations?.data || [],
    [userInvitations?.data]
  );
  const isLoading = !isLoaded || userInvitations?.isLoading;

  const signInRedirectUrl = useMemo(() => {
    const query = searchParams.toString();
    return query ? `/accept-invitation?${query}` : '/accept-invitation';
  }, [searchParams]);

  useEffect(() => {
    if (isLoading || autoAcceptedRef.current) return;
    if (invitations.length !== 1) return;

    autoAcceptedRef.current = true;
    setAcceptingId(invitations[0].id);
    invitations[0]
      .accept()
      .then(() => {
        navigate(APP_REDIRECT_PATH);
      })
      .catch((acceptError) => {
        const message =
          acceptError?.errors?.[0]?.message ||
          acceptError?.message ||
          'Unable to accept invitation.';
        setError(message);
        setAcceptingId(null);
        autoAcceptedRef.current = false;
      });
  }, [invitations, isLoading, navigate]);

  const handleAccept = async (invitation) => {
    setError('');
    setAcceptingId(invitation.id);
    try {
      await invitation.accept();
      navigate(APP_REDIRECT_PATH);
    } catch (acceptError) {
      const message =
        acceptError?.errors?.[0]?.message ||
        acceptError?.message ||
        'Unable to accept invitation.';
      setError(message);
    } finally {
      setAcceptingId(null);
    }
  };

  const renderInvitationName = (invitation) =>
    invitation?.organization?.name ||
    invitation?.publicOrganizationData?.name ||
    invitation?.organizationName ||
    'Family';

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 text-center">
      <SignedOut>
        <div className="max-w-md w-full space-y-4">
          <h1 className="text-2xl font-semibold text-gray-900">
            Accept your family invite
          </h1>
          <p className="text-gray-600">
            Sign in to accept your invitation and continue in the app.
          </p>
          <div className="flex justify-center">
            <SignIn redirectUrl={signInRedirectUrl} />
          </div>
        </div>
      </SignedOut>

      <SignedIn>
        <div className="max-w-md w-full space-y-4">
          <h1 className="text-2xl font-semibold text-gray-900">
            Accept your family invite
          </h1>
          {ticket && (
            <p className="text-sm text-gray-500">
              Invitation status: {status || 'pending'}
            </p>
          )}

          {isLoading && <p className="text-gray-600">Loading invitations…</p>}

          {!isLoading && invitations.length === 0 && (
            <div className="space-y-3">
              <p className="text-gray-600">
                No pending invitations were found for this account.
              </p>
              <button
                className="w-full rounded-lg bg-saverly-primary text-white py-3 font-semibold"
                onClick={() => navigate(APP_REDIRECT_PATH)}
                type="button"
              >
                Open Saverly
              </button>
            </div>
          )}

          {!isLoading && invitations.length > 0 && (
            <div className="space-y-3">
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="border border-gray-200 rounded-xl p-4 flex items-center justify-between gap-3"
                >
                  <div className="text-left">
                    <p className="text-gray-900 font-semibold">
                      {renderInvitationName(invitation)}
                    </p>
                    <p className="text-sm text-gray-500">
                      Role: {invitation.role?.replace('org:', '') || 'member'}
                    </p>
                  </div>
                  <button
                    className="rounded-lg bg-saverly-primary text-white px-4 py-2 font-semibold disabled:opacity-60"
                    onClick={() => handleAccept(invitation)}
                    disabled={acceptingId === invitation.id}
                    type="button"
                  >
                    {acceptingId === invitation.id ? 'Accepting…' : 'Accept'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      </SignedIn>
    </div>
  );
};

export default AcceptInvitationPage;
